import { Box } from "@mui/material";
import { type MouseEvent, useCallback, useMemo, useRef, useState } from "react";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import type { NurseInShift } from "../../../../utils/models";
import { NurseShiftBadges } from "../../../NurseShiftBadges/NurseShiftBadges";
import { DailyGapsQuickActionButton } from "./DailyGapsQuickActionButton";
import { NurseInShiftNotesInput } from "./NurseInShiftNotesInput";
import { DailyGapsQuickAction } from "./utils";

type Props = {
	nurseInShift: NurseInShift;
	isHovered: boolean;
	onQuickActionSelected: (action: DailyGapsQuickAction, anchorEl: HTMLElement) => void;
	hideActionsWhenNotHovered: boolean;
	/** Whether this block's Remove quick action is the call-off walkthrough's one spotlit trigger (see `gapsCallOffAnchorItem`). */
	isCallOffWalkthroughAnchor: boolean;
};

const getRelevantQuickActions = (): DailyGapsQuickAction[] => {
	const actions: DailyGapsQuickAction[] = [];
	actions.push(DailyGapsQuickAction.TIME);
	actions.push(DailyGapsQuickAction.NOTE);
	actions.push(DailyGapsQuickAction.REMOVE);
	return actions;
};

export const NurseInShiftTrailingNode = ({ nurseInShift, isHovered, onQuickActionSelected, hideActionsWhenNotHovered, isCallOffWalkthroughAnchor }: Props) => {
	const { getWeekIndexByDateTime } = useHospitalData();
	const relevantQuickActions = useMemo(() => getRelevantQuickActions(), []);
	const [clickedQuickAction, setClickedQuickAction] = useState<DailyGapsQuickAction | null>(null);
	const renderNotes = clickedQuickAction === DailyGapsQuickAction.NOTE;
	const rootRef = useRef<HTMLElement | null>(null);

	const resetClickedQuickAction = useCallback(() => {
		setClickedQuickAction(null);
	}, []);
	const handleQuickActionClick = useCallback(
		(e: MouseEvent<HTMLElement>, action: DailyGapsQuickAction) => {
			e.stopPropagation();
			setClickedQuickAction(action);
			if (rootRef.current && action !== DailyGapsQuickAction.NOTE) {
				onQuickActionSelected(action, rootRef.current);
			}
		},
		[onQuickActionSelected],
	);

	return (
		<Box
			ref={rootRef}
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				flex: 1,
				justifyContent: "end",
			}}
		>
			{renderNotes && <NurseInShiftNotesInput nurseInShift={nurseInShift} isParentHovered={isHovered} onUpdateComplete={resetClickedQuickAction} />}
			{!renderNotes &&
				(!hideActionsWhenNotHovered || isHovered) &&
				relevantQuickActions.map((action) => (
					<DailyGapsQuickActionButton
						key={action}
						isHovered={isHovered}
						quickAction={action}
						onClick={handleQuickActionClick}
						isCallOffWalkthroughAnchor={isCallOffWalkthroughAnchor && action === DailyGapsQuickAction.REMOVE}
					/>
				))}
			<NurseShiftBadges
				shift={nurseInShift}
				weekIndex={getWeekIndexByDateTime(nurseInShift.startTime)}
				ignoreTraineeOrOrientation={true}
				chipSx={{
					fontSize: "10px",
					height: "fit-content",
				}}
			/>
		</Box>
	);
};
